/**
 * Featured cards section.
 *
 * Per-card behavior:
 *   - Swatches hidden by default (CSS via [data-state="default"]).
 *   - Hover/focus the card → state becomes "hover" → swatches revealed.
 *   - Click a swatch → state becomes "selected", swatch ringed, image swaps,
 *     subtitle becomes the variant title, CTA flips to "Add to Bag $price".
 *   - Click CTA in selected state → POST to KROWN.settings.routes.cart_add_url,
 *     refresh AjaxCartForm/AjaxCartSubtotal/header counts (mirroring how the
 *     theme's own quick-buy works), then trigger the existing cart drawer or
 *     mini-cart popup.
 *
 * Carousel:
 *   - Native horizontal scroll with snap points.
 *   - Prev/Next buttons scrollBy one card width on desktop only.
 *   - Buttons get aria-disabled when at the start/end of the rail.
 */

(function () {
  if (window.__featuredCardsInit) return;
  window.__featuredCardsInit = true;

  document.addEventListener('DOMContentLoaded', initAll);
  // Re-init when Shopify theme editor swaps a section.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector('[data-featured-cards]')) {
      initAll(e.target);
    }
  });

  function initAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-featured-cards]').forEach((section) => {
      if (section.__fcInited) return;
      section.__fcInited = true;
      initSection(section);
    });
  }

  function initSection(section) {
    section.querySelectorAll('[data-fc-card]').forEach(initCard);
    initRail(section);
  }

  // ---------- Card interaction ----------
  function initCard(card) {
    const swatches = card.querySelectorAll('[data-fc-swatch]');
    const hasSwatches = swatches.length > 0;

    const cta = card.querySelector('[data-fc-cta]');
    const ctaLabel = card.querySelector('[data-fc-cta-label]');
    const ctaPrice = card.querySelector('[data-fc-cta-price]');
    const subtitle = card.querySelector('[data-fc-subtitle]');
    const image = card.querySelector('[data-fc-image]');

    const defaults = {
      label: cta?.dataset.defaultLabel || 'Select Color',
      addLabel: cta?.dataset.addLabel || 'Add to Bag',
      soldOutLabel: cta?.dataset.soldOutLabel || 'Sold out',
      price: card.dataset.defaultPrice || '',
      subtitle: card.dataset.defaultSubtitle || '',
      image: card.dataset.defaultImage || (image ? image.getAttribute('src') : ''),
    };

    // Hover / focus state — only when not already "selected", and only
    // meaningful when there are swatches to reveal.
    if (hasSwatches) {
      card.addEventListener('mouseenter', () => {
        if (card.dataset.state !== 'selected') card.dataset.state = 'hover';
      });
      card.addEventListener('mouseleave', () => {
        if (card.dataset.state !== 'selected') card.dataset.state = 'default';
      });
      card.addEventListener('focusin', () => {
        if (card.dataset.state !== 'selected') card.dataset.state = 'hover';
      });
      card.addEventListener('focusout', (e) => {
        if (card.contains(e.relatedTarget)) return;
        if (card.dataset.state !== 'selected') card.dataset.state = 'default';
      });

      // Swatch selection.
      swatches.forEach((swatch) => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          selectSwatch(swatch);
        });
        swatch.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectSwatch(swatch);
          }
        });
      });
    }

    function selectSwatch(swatch) {
      swatches.forEach((s) => s.setAttribute('aria-selected', 'false'));
      swatch.setAttribute('aria-selected', 'true');
      card.dataset.state = 'selected';

      const variantTitle = swatch.dataset.variantTitle || '';
      const variantPrice = swatch.dataset.variantPrice || defaults.price;
      const variantImage = swatch.dataset.variantImage;
      const isAvailable = swatch.dataset.variantAvailable !== 'false';

      if (subtitle) subtitle.textContent = variantTitle;
      if (image && variantImage) {
        image.removeAttribute('srcset');
        image.setAttribute('src', variantImage);
      }
      if (ctaPrice) ctaPrice.textContent = variantPrice;
      if (ctaLabel) {
        ctaLabel.textContent = isAvailable ? defaults.addLabel : defaults.soldOutLabel;
      }
      if (cta) {
        cta.dataset.variantId = swatch.dataset.variantId || '';
        cta.disabled = !isAvailable;
        cta.classList.toggle('is-sold-out', !isAvailable);
      }
    }

    // CTA click — bound unconditionally so the button always does something:
    //   - state "selected" (swatch picked) → AJAX add the picked variant
    //   - state "ready"    (single-variant) → AJAX add the prefilled variant
    //   - state "default"/"hover"            → go to PDP
    if (cta) {
      cta.addEventListener('click', async (e) => {
        const state = card.dataset.state;
        const canAdd = state === 'selected' || state === 'ready';
        if (!canAdd) {
          e.preventDefault();
          const url = card.dataset.productUrl;
          if (url) window.location.href = url;
          return;
        }
        const variantId = cta.dataset.variantId;
        if (!variantId) return;
        cta.classList.add('is-loading');
        try {
          await addVariantToCart(variantId);
        } catch (err) {
          console.error('[featured-cards] add to cart failed:', err);
        } finally {
          cta.classList.remove('is-loading');
        }
      });
    }
  }

  // ---------- AJAX add to cart, integrated with KROWN cart UI ----------
  async function addVariantToCart(variantId) {
    const settings = (window.KROWN && window.KROWN.settings) || {};
    const addUrl = (settings.routes && settings.routes.cart_add_url) || '/cart/add';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const addRes = await fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const addJson = await addRes.json().catch(() => ({}));
    if (!addRes.ok) {
      const msg = addJson.description || addJson.message || `HTTP ${addRes.status}`;
      throw new Error(msg);
    }

    // Pull updated cart fragment, mirroring component-quick-buy.js.
    const cartRes = await fetch('?section_id=helper-cart');
    const cartHtml = await cartRes.text();
    const dom = new DOMParser().parseFromString(cartHtml, 'text/html');

    const ajaxCartForm = document.getElementById('AjaxCartForm');
    const newCartForm = dom.getElementById('AjaxCartForm');
    if (ajaxCartForm && newCartForm) {
      ajaxCartForm.innerHTML = newCartForm.innerHTML;
      if (typeof ajaxCartForm.ajaxifyCartItems === 'function') {
        ajaxCartForm.ajaxifyCartItems();
      }
    }
    const ajaxCartSubtotal = document.getElementById('AjaxCartSubtotal');
    const newCartSubtotal = dom.getElementById('AjaxCartSubtotal');
    if (ajaxCartSubtotal && newCartSubtotal) {
      ajaxCartSubtotal.innerHTML = newCartSubtotal.innerHTML;
    }

    const newCartCount = dom.querySelector('[data-cart-count]');
    if (newCartCount) {
      document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
        el.innerHTML = newCartCount.innerHTML;
      });
    }
    const newCartTotal = dom.querySelector('[data-cart-total]');
    if (newCartTotal) {
      document.querySelectorAll('[data-header-cart-total]').forEach((el) => {
        el.innerHTML = newCartTotal.innerHTML;
      });
    }

    if (document.getElementById('cart-recommendations-sidebar')) {
      const recs = document.getElementById('cart-recommendations-sidebar');
      if (typeof recs.generateRecommendations === 'function') recs.generateRecommendations();
    }

    // Reveal cart UI: prefer popup → drawer → cart page.
    if (document.body.classList.contains('template-cart')) {
      window.location.href = (settings.routes && settings.routes.cart_url) || '/cart';
      return;
    }
    const popup = document.getElementById('mini-cart-popup');
    const drawer = document.getElementById('site-cart-sidebar');
    if (settings.cart_popup === 'true' && popup && typeof popup.show === 'function') {
      popup.show();
      if (popup.dataset.hide && popup.dataset.hide !== '0') {
        setTimeout(() => {
          if (typeof popup.hide === 'function') popup.hide();
        }, parseInt(popup.dataset.hide, 10));
      }
    } else if (settings.cart_action === 'overlay' && drawer && typeof drawer.show === 'function') {
      drawer.show();
    } else {
      window.location.href = (settings.routes && settings.routes.cart_url) || '/cart';
    }
  }

  // ---------- Carousel arrows ----------
  function initRail(section) {
    const rail = section.querySelector('[data-fc-rail]');
    if (!rail) return;
    const prev = section.querySelector('[data-fc-nav="prev"]');
    const next = section.querySelector('[data-fc-nav="next"]');
    if (!prev && !next) return;

    function step() {
      const card = rail.querySelector('[data-fc-card]');
      if (!card) return rail.clientWidth * 0.8;
      const styles = window.getComputedStyle(rail);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    if (prev) prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));

    function updateNavState() {
      const max = rail.scrollWidth - rail.clientWidth;
      const atStart = rail.scrollLeft <= 1;
      const atEnd = rail.scrollLeft >= max - 1;
      if (prev) prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      if (next) next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }

    rail.addEventListener('scroll', () => requestAnimationFrame(updateNavState), { passive: true });
    window.addEventListener('resize', () => requestAnimationFrame(updateNavState));
    updateNavState();
  }
})();
